/* M6.6.2 — Raise Dispute drawer. Creates one structured Ops/Tech verification
   task per AWB. POC is auto-filled from partner onboarding. Mismatch type +
   note + assignee are required; optional evidence drop. */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined'
import {SideDrawer, FileDrop, SearchSelect, useToast, inr, money, fmtDate} from '../components'
import {MISMATCH_TYPES, DISPUTES, makeDispute, podPocFor} from './codLogic'

const ASSIGNEES = ['ops@prozo.com', 'tech@prozo.com', 'analyst@prozo.com']

export default function RaiseDisputeDrawer({open, rows, onClose, onDone}) {
  const toast = useToast()
  const [type, setType] = useState('')
  const [assignee, setAssignee] = useState(ASSIGNEES[0])
  const [note, setNote] = useState('')
  const [errType, setErrType] = useState(false)
  const [errNote, setErrNote] = useState(false)

  if (!rows || !rows.length) return null
  const multi = rows.length > 1
  const first = rows[0]
  const poc = podPocFor(first.courier)
  const atRisk = rows.reduce((s, r) => s + r.codValue, 0)

  const reset = () => {
    setType('')
    setAssignee(ASSIGNEES[0])
    setNote('')
    setErrType(false)
    setErrNote(false)
  }
  const close = () => {
    reset()
    onClose()
  }

  const submit = () => {
    let ok = true
    if (!type) {
      setErrType(true)
      ok = false
    }
    if (!note.trim()) {
      setErrNote(true)
      ok = false
    }
    if (!ok) {
      toast('Complete the required fields.', 'error')
      return
    }
    rows.forEach((r) => {
      DISPUTES.unshift(makeDispute({row: r, mismatch: type, owner: assignee, note: note.trim()}))
    })
    toast(
      (multi ? rows.length + ' disputes' : 'Dispute') +
        ' raised. Ops/Tech task created for ' +
        assignee +
        '.',
      'success'
    )
    reset()
    onClose()
    if (onDone) onDone()
  }

  return (
    <SideDrawer
      open={open}
      onClose={close}
      title='Raise Dispute'
      width={520}
      footer={
        <>
          <button className='btn' onClick={close}>
            Cancel
          </button>
          <button className='btn btn-primary' onClick={submit}>
            <AssignmentLateOutlinedIcon style={{width: 16, height: 16}} />
            Raise {multi ? rows.length + ' Disputes' : 'Dispute'}
          </button>
        </>
      }
    >
      <div className='warnbox info' style={{margin: '0 0 14px'}}>
        <span style={{display: 'flex'}}>
          <InfoOutlinedIcon style={{width: 16, height: 16}} />
        </span>
        <div>
          Raising a dispute creates a structured Ops/Tech task to verify the true status with the
          courier. You do <b>not</b> need shipment status-edit rights.
        </div>
      </div>

      <div className='seclabel' style={{marginBottom: 8}}>
        Shipment{multi ? 's (' + rows.length + ' selected)' : ''}
      </div>
      {multi ? (
        <div className='card card-pad' style={{background: '#FAFBFC', marginBottom: 14}}>
          <div className='row between center'>
            <div>
              <b>{rows.length} AWBs</b> · one dispute will be created per AWB
            </div>
            <div style={{fontWeight: 800, color: 'var(--error)'}}>{money(atRisk)} at risk</div>
          </div>
        </div>
      ) : (
        <div className='dl' style={{gridTemplateColumns: 'auto 1fr', marginBottom: 14}}>
          <dt>AWB</dt>
          <dd className='mono'>{first.awb}</dd>
          <dt>Courier / CP code</dt>
          <dd>
            {first.courier} ·{' '}
            <span className='mono' style={{fontSize: 12}}>
              {first.cpCode}
            </span>
          </dd>
          <dt>COD Value</dt>
          <dd>
            <b>{inr(first.codValue)}</b>
          </dd>
          <dt>ProShip status</dt>
          <dd>Delivered (marked {fmtDate(first.proshipMarked)})</dd>
          <dt>Courier status</dt>
          <dd>{first.courierStatus}</dd>
        </div>
      )}

      <div className='seclabel' style={{marginBottom: 8}}>
        COD Remittance POC{' '}
        <span className='muted' style={{textTransform: 'none', fontWeight: 400}}>
          · auto-filled from onboarding
        </span>
      </div>
      <div className='card card-pad' style={{background: '#FAFBFC', marginBottom: 16}}>
        {poc ? (
          <div className='dl' style={{gridTemplateColumns: 'auto 1fr', margin: 0}}>
            <dt>Name</dt>
            <dd>
              <b>{poc.name}</b>
            </dd>
            <dt>Email</dt>
            <dd>
              <a className='lnk' href={'mailto:' + poc.email}>
                {poc.email}
              </a>
            </dd>
            <dt>Mobile</dt>
            <dd className='mono' style={{fontSize: 12}}>
              {poc.m1}
            </dd>
          </div>
        ) : (
          <div className='muted' style={{fontSize: 13}}>
            No COD Remittance POC on file for {first.courier} — add one in Partner Onboarding.
          </div>
        )}
      </div>

      <div className={'field' + (errType ? ' invalid' : '')}>
        <label>
          Mismatch type <span className='req'>*</span>
        </label>
        <SearchSelect
          value={type}
          invalid={errType}
          placeholder='Search…'
          options={MISMATCH_TYPES}
          onChange={(v) => {
            setType(v)
            setErrType(false)
          }}
        />
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Determines the verification task routed to Ops/Tech.
        </div>
        <div className='vmsg'>Select a mismatch type.</div>
      </div>

      <div className='field'>
        <label>
          Ops / Tech assignee <span className='req'>*</span>
        </label>
        <SearchSelect value={assignee} options={ASSIGNEES} onChange={(v) => setAssignee(v)} />
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Configured internal assignee who will verify the true status with the courier.
        </div>
      </div>

      <div className={'field' + (errNote ? ' invalid' : '')}>
        <label>
          Note <span className='req'>*</span>
        </label>
        <textarea
          className={'input' + (errNote ? ' err' : '')}
          rows={3}
          value={note}
          placeholder='e.g. POD confirms delivery; courier MIS shows RTO. Requesting status correction + COD remittance.'
          onChange={(e) => {
            setNote(e.target.value)
            setErrNote(false)
          }}
        />
        <div className='vmsg'>A note is required.</div>
      </div>

      <div className='field'>
        <label>
          Evidence <span className='opt'>(optional)</span>
        </label>
        <FileDrop
          accept='.png,.jpg,.jpeg,.pdf'
          title='Attach POD / courier screenshot / email'
          hint='PNG, JPG, PDF · optional'
        />
      </div>
    </SideDrawer>
  )
}
RaiseDisputeDrawer.propTypes = {
  open: PropTypes.bool,
  rows: PropTypes.array,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
}
